/*
 * Comandos de edición del motor, llamados desde FUERA del iframe (mismo origen:
 * el motor se sirve en /office/engine/ de este dominio).
 *
 * Existe para la app del teléfono: allí la barra del motor —pensada para ratón y
 * pantalla ancha— se esconde y la dibuja la app en nativo, que manda aquí lo que
 * la persona pulsa. Son las MISMAS llamadas que hace la barra del editor
 * (`put_TextPrBold`, `put_PrAlign`…), no una reimplementación.
 *
 * Los números (alineación, interlineado, insertar filas) se leen del propio
 * motor cuando están disponibles; los de respaldo son los de la versión 9.4,
 * comprobados contra el editor.
 */

/** API interna del editor dentro del iframe. `null` si aún no cargó. */
export function motorDe(iframe) {
  try {
    const win = iframe?.contentWindow;
    return win?.Asc?.editor || win?.editor || null;
  } catch {
    return null; // otro origen (no debería pasar) o iframe ya destruido
  }
}

/** word | cell | slide | pdf | visio, según el editor abierto. */
export function tipoDeMotor(iframe) {
  const ruta = (() => {
    try {
      return iframe?.contentDocument?.location?.pathname || '';
    } catch {
      return '';
    }
  })();
  if (ruta.includes('/spreadsheeteditor/')) return 'cell';
  if (ruta.includes('/presentationeditor/')) return 'slide';
  if (ruta.includes('/pdfeditor/')) return 'pdf';
  if (ruta.includes('/visioeditor/')) return 'visio';
  return 'word';
}

const num = (valor, respaldo) => (typeof valor === 'number' ? valor : respaldo);

function constantes(iframe) {
  const win = iframe?.contentWindow || {};
  const comun = win.AscCommon || {};
  const asc = win.Asc || {};
  return {
    align: {
      left: num(comun.align_Left, 1),
      right: num(comun.align_Right, 0),
      center: num(comun.align_Center, 2),
      justify: num(comun.align_Justify, 3),
    },
    lineruleAuto: num(asc.linerule_Auto, 1),
    insertar: asc.c_oAscInsertOptions || { InsertColumns: 3, InsertRows: 4 },
    borrar: asc.c_oAscDeleteOptions || { DeleteColumns: 3, DeleteRows: 4 },
    Color: asc.asc_CColor,
    Hipervinculo: asc.CHyperlinkProperty,
  };
}

/** Color del motor a partir de "#rrggbb". */
function colorDelMotor(iframe, hex) {
  const { Color } = constantes(iframe);
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
  if (!Color || !m) return null;
  const color = new Color();
  color.put_r(parseInt(m[1], 16));
  color.put_g(parseInt(m[2], 16));
  color.put_b(parseInt(m[3], 16));
  color.put_auto?.(false);
  return color;
}

/**
 * Ejecuta un comando. Devuelve `true` si el motor lo aceptó.
 * Los nombres son los del protocolo con la app (`app/lib/core/office_host.dart`).
 */
export function ejecutar(iframe, cmd, valor) {
  const api = motorDe(iframe);
  if (!api) return false;
  const tipo = tipoDeMotor(iframe);
  const hoja = tipo === 'cell';
  const c = constantes(iframe);

  const texto = (metodoDoc, metodoHoja, v) => {
    if (hoja) api[metodoHoja]?.(v);
    else api[metodoDoc]?.(v);
  };

  switch (cmd) {
    case 'undo':
      api.Undo?.();
      return true;
    case 'redo':
      api.Redo?.();
      return true;
    case 'bold':
      texto('put_TextPrBold', 'asc_setCellBold', !!valor);
      return true;
    case 'italic':
      texto('put_TextPrItalic', 'asc_setCellItalic', !!valor);
      return true;
    case 'underline':
      texto('put_TextPrUnderline', 'asc_setCellUnderline', !!valor);
      return true;
    case 'strike':
      texto('put_TextPrStrikeout', 'asc_setCellStrikeout', !!valor);
      return true;
    case 'fontSize': {
      const tam = Math.max(6, Math.min(96, Number(valor) || 11));
      texto('put_TextPrFontSize', 'asc_setCellFontSize', tam);
      return true;
    }
    case 'color': {
      const color = colorDelMotor(iframe, valor);
      if (!color) return false;
      if (hoja) api.asc_setCellTextColor?.(color);
      else api.put_TextColor?.(color);
      return true;
    }
    case 'align': {
      // En la hoja la alineación va por nombre; en documento y presentación, por número
      if (hoja) {
        api.asc_setCellAlign?.(valor);
        return true;
      }
      const n = c.align[valor];
      if (n === undefined) return false;
      api.put_PrAlign?.(n);
      return true;
    }
    case 'valign':
      if (!hoja) return false;
      api.asc_setCellVertAlign?.(valor);
      return true;
    case 'list': {
      if (hoja) return false;
      // viñetas = (0,0) · numerada = (1,0) · quitar = (-1,-1), como la barra del motor
      const mapa = { bullet: [0, 0], number: [1, 0], none: [-1, -1] };
      const par = mapa[valor] || mapa.none;
      api.put_ListType?.(par[0], par[1]);
      return true;
    }
    case 'indent':
      if (hoja) return false;
      if (valor === 'more') api.IncreaseIndent?.();
      else api.DecreaseIndent?.();
      return true;
    case 'lineSpacing': {
      if (hoja) return false;
      const v = Number(valor) || 1;
      api.put_PrLineSpacing?.(c.lineruleAuto, v);
      return true;
    }
    case 'numberFormat':
      if (!hoja) return false;
      api.asc_setCellFormat?.(valor);
      return true;
    case 'mergeCells':
      if (!hoja) return false;
      api.asc_mergeCells?.();
      return true;
    case 'filter':
      if (!hoja) return false;
      api.asc_addAutoFilter?.('TableStyleLight9');
      return true;
    case 'insertRows':
      if (!hoja) return false;
      api.asc_insertCells?.(c.insertar.InsertRows);
      return true;
    case 'insertColumns':
      if (!hoja) return false;
      api.asc_insertCells?.(c.insertar.InsertColumns);
      return true;
    case 'deleteRows':
      if (!hoja) return false;
      api.asc_deleteCells?.(c.borrar.DeleteRows);
      return true;
    case 'deleteColumns':
      if (!hoja) return false;
      api.asc_deleteCells?.(c.borrar.DeleteColumns);
      return true;
    case 'table': {
      if (typeof api.put_Table !== 'function') return false;
      const { cols = 3, rows = 3 } = valor || {};
      api.put_Table(Math.max(1, cols), Math.max(1, rows));
      return true;
    }
    case 'image':
      // Abre el selector del sistema; en la app lo atiende el selector nativo
      if (typeof api.AddImage !== 'function') return false;
      api.AddImage();
      return true;
    case 'link': {
      const { Hipervinculo } = c;
      const url = (valor?.url || '').trim();
      if (!Hipervinculo || !url) return false;
      const props = new Hipervinculo();
      props.put_Value(url);
      if (valor?.texto) props.put_Text(valor.texto);
      props.put_ToolTip?.(valor?.texto || url);
      api.add_Hyperlink?.(props);
      return true;
    }
    case 'pageBreak':
      if (typeof api.put_AddPageBreak !== 'function') return false;
      api.put_AddPageBreak();
      return true;
    case 'slideNew':
      if (tipo !== 'slide') return false;
      api.AddSlide?.();
      return true;
    case 'slideDelete':
      if (tipo !== 'slide') return false;
      api.DeleteSlide?.();
      return true;
    case 'zoomIn':
      api.zoomIn?.();
      return true;
    case 'zoomOut':
      api.zoomOut?.();
      return true;
    case 'zoomFit':
      (tipo === 'slide' ? api.zoomFitToPage : api.zoomFitToWidth)?.call(api);
      return true;
    default:
      return false;
  }
}

/**
 * Avisa del estado de la selección (negrita, alineación, tamaño, deshacer…)
 * para que los botones de la app se enciendan solos. Devuelve una función que
 * deja de escuchar.
 */
export function escucharEstado(iframe, alCambiar) {
  const api = motorDe(iframe);
  if (!api?.asc_registerCallback) return () => {};
  const c = constantes(iframe);
  const porNumero = Object.fromEntries(Object.entries(c.align).map(([k, v]) => [v, k]));
  const estado = {
    bold: false, italic: false, underline: false, strike: false,
    align: 'left', fontSize: 11, canUndo: false, canRedo: false,
  };
  let pendiente = null;
  const avisar = () => {
    // El motor dispara varios avisos seguidos por un solo clic: se agrupan
    clearTimeout(pendiente);
    pendiente = setTimeout(() => alCambiar({ ...estado }), 60);
  };
  const escuchas = [
    ['asc_onBold', (v) => { estado.bold = !!v; }],
    ['asc_onItalic', (v) => { estado.italic = !!v; }],
    ['asc_onUnderline', (v) => { estado.underline = !!v; }],
    ['asc_onStrikeout', (v) => { estado.strike = !!v; }],
    ['asc_onFontSize', (v) => { estado.fontSize = Number(v) || estado.fontSize; }],
    ['asc_onPrAlign', (v) => { estado.align = porNumero[v] || estado.align; }],
    ['asc_onCanUndo', (v) => { estado.canUndo = !!v; }],
    ['asc_onCanRedo', (v) => { estado.canRedo = !!v; }],
  ];
  for (const [nombre, aplicar] of escuchas) {
    try {
      api.asc_registerCallback(nombre, (v) => {
        aplicar(v);
        avisar();
      });
    } catch {
      /* ese aviso no existe en este editor: el resto sigue funcionando */
    }
  }
  avisar();
  return () => {
    clearTimeout(pendiente);
    for (const [nombre] of escuchas) {
      try {
        api.asc_unregisterCallback?.(nombre);
      } catch {
        /* el editor ya no está */
      }
    }
  };
}
